import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  WindowsLogo, AppleLogo, Lightning, ShieldCheck, Download as DownloadIcon,
  Cpu, FileText, Copy, Check
} from "@phosphor-icons/react";

export default function Download() {
  const [latest, setLatest] = useState(null);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plugin/latest").then(({ data }) => setLatest(data));
  }, []);

  const start = async (platform) => {
    if (!user) { navigate(`/auth?mode=signup&redirect=/download`); return; }
    setBusy(platform);
    try {
      const { data } = await api.get(`/plugin/download/${platform}`);
      setMeta((m) => ({ ...m, [platform]: data }));
      // Trigger native browser download via the signed URL (token in query string)
      const url = data.download_url.startsWith("http")
        ? data.download_url
        : `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = data.file_name;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  if (!latest) return <div className="min-h-screen flex items-center justify-center text-ink-dim">LOADING…</div>;

  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16" data-testid="download-page">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-4">◢ INSTALL THE PLUGIN</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
          DOWNLOAD <span className="text-brand-red neon-red">BEAT-LAB.</span>
        </h1>
        <p className="text-ink-mute text-sm mt-6 max-w-xl mx-auto leading-relaxed">
          Universal builds for Windows + Mac. VST3, VST2, and AU formats included.
          Free demo with no time limit — full features unlock with any subscription.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
          <span className="text-brand-red">BUILD {latest.windows.version}</span>
          <span>·</span>
          <span className="text-brand-green flex items-center gap-1"><ShieldCheck size={12} weight="bold" /> CODE-SIGNED</span>
          <span>·</span>
          <span className="text-brand-purple">SHA-256 VERIFIED</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mb-10">
        <PlatformCard
          platform="windows"
          Icon={WindowsLogo}
          color="text-brand-cyan"
          accent="#00C2FF"
          build={latest.windows}
          busy={busy === "windows"}
          meta={meta.windows}
          onDownload={() => start("windows")}
          onCopy={copy}
          copied={copied}
        />
        <PlatformCard
          platform="macos"
          Icon={AppleLogo}
          color="text-white"
          accent="#8B00FF"
          build={latest.macos}
          busy={busy === "macos"}
          meta={meta.macos}
          onDownload={() => start("macos")}
          onCopy={copy}
          copied={copied}
        />
      </div>

      {/* Compatibility */}
      <div className="border border-zinc-800 bg-bg-surface grain p-6 mb-10" data-testid="download-compat">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-4 flex items-center gap-2">
          <Cpu size={12} weight="bold" className="text-brand-red" /> TESTED DAWS
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-zinc-900 border border-zinc-900">
          {["FL Studio", "Ableton Live", "Logic Pro", "Pro Tools", "Studio One", "Bitwig", "Cubase", "Reaper"].map((d) => (
            <div key={d} className="bg-bg-surface p-3 text-center">
              <div className="text-[9px] tracking-[0.25em] uppercase text-ink-dim">{d}</div>
              <Check size={14} weight="bold" className="text-brand-green mx-auto mt-1" />
            </div>
          ))}
        </div>
      </div>

      {/* CTA back */}
      <div className="text-center">
        <Link to="/playground" className="inline-flex items-center gap-2 border border-zinc-700 px-6 py-3 text-xs tracking-[0.3em] uppercase hover:border-brand-red hover:text-brand-red">
          <Lightning size={14} weight="fill" /> TRY THE WEB SYNTH FIRST →
        </Link>
      </div>
    </div>
  );
}

function PlatformCard({ platform, Icon, color, accent, build, busy, meta, onDownload, onCopy, copied }) {
  return (
    <div className="bg-bg-surface p-8 lg:p-10 relative overflow-hidden group" data-testid={`download-${platform}`}>
      <div
        className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none opacity-20 group-hover:opacity-40 transition-opacity"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-3 mb-4">
          <Icon size={36} weight="fill" className={color} />
          <div>
            <div className="font-display font-black uppercase tracking-tighter text-3xl">
              {platform === "windows" ? "WINDOWS" : "MACOS"}
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mt-0.5">
              {build.os_support}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 my-6">
          <Spec label="VERSION" value={build.version} />
          <Spec label="SIZE" value={`${build.file_size_mb} MB`} />
          <Spec label="FORMATS" value={build.formats.join(" · ")} />
          <Spec label="ARCH" value={platform === "windows" ? "x64" : "Universal"} />
        </div>

        <button
          onClick={onDownload}
          disabled={busy}
          data-testid={`download-btn-${platform}`}
          className="w-full bg-brand-red text-black text-xs font-bold tracking-[0.3em] uppercase py-4 hover:bg-white disabled:opacity-60 transition-colors btn-glitch flex items-center justify-center gap-2"
        >
          <DownloadIcon size={14} weight="bold" />
          {busy ? "PREPARING…" : `DOWNLOAD ${platform === "windows" ? ".ZIP" : ".DMG"}`}
        </button>

        {meta && (
          <div className="mt-5 border border-brand-red/40 bg-brand-red/5 p-4 text-[10px] font-mono space-y-2" data-testid={`download-meta-${platform}`}>
            <div className="text-[9px] tracking-[0.3em] uppercase text-brand-red mb-1">▸ INSTALLER MANIFEST DOWNLOADED</div>
            <CopyRow label="FILE" value={meta.file_name} onCopy={onCopy} k={`${platform}-file`} copied={copied} />
            <CopyRow label="LICENSE KEY" value={meta.license_key} onCopy={onCopy} k={`${platform}-key`} copied={copied} />
            <CopyRow label="SHA-256" value={meta.sha256} onCopy={onCopy} k={`${platform}-sha`} copied={copied} truncate />
            <p className="text-ink-mute leading-relaxed pt-2 border-t border-zinc-900">{meta.instructions}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div className="bg-bg-surface p-3">
      <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim">{label}</div>
      <div className="font-display text-sm font-bold mt-1 truncate">{value}</div>
    </div>
  );
}

function CopyRow({ label, value, onCopy, k, copied, truncate }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-ink-dim text-[9px] tracking-[0.3em] uppercase">{label}</span>
      <code className={`flex-1 text-white ${truncate ? "truncate" : ""} text-right`}>{value}</code>
      <button onClick={() => onCopy(value, k)} className="text-ink-mute hover:text-brand-red p-1" data-testid={`copy-${k}`}>
        {copied === k ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
      </button>
    </div>
  );
}
